import type {
  AdminLeadPipelineCursor,
  AdminLeadPipelineItem,
  AdminLeadPipelineLanePage,
  AdminLeadPipelineMoveOutcome,
  AdminLeadPipelineMoveRequest,
  IdempotencyKey,
  LeadId,
  LeadPipelineStage,
} from "@whitehaven/contracts";
import type { AuthorizationSubject } from "@whitehaven/permissions";
import { ContentState } from "@whitehaven/ui";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import {
  adminCrmAuthorizationKey,
  canAccessAdminCrm,
} from "./admin-crm-authorization";
import {
  applyConfirmedAdminLeadPipelineMove,
  buildOrderedAdminLeadPipelineLanes,
  isAdminLeadPipelineLanePageConsistent,
  isConfirmedAdminLeadPipelineMoveConsistent,
  mergeAdminLeadPipelineItems,
} from "./admin-lead-pipeline-model";
import { AdminCrmViewNavigation } from "./AdminCrmViewNavigation";
import {
  AdminLeadPipelineView,
  type AdminLeadPipelineFeedback,
  type AdminLeadPipelineLaneViewState,
} from "./AdminLeadPipelineView";
import {
  ADMIN_LEAD_PIPELINE_PAGE_SIZE,
  type AdminLeadPipelineMutationGateway,
  type AdminLeadPipelineReadGateway,
} from "./lead-pipeline-gateway";
import {
  isLeadPipelineStage,
  leadPipelineStageOrder,
} from "./pipeline-stage-model";

interface AdminLeadPipelineRouteProps {
  readonly subject: AuthorizationSubject | null;
  readonly readGateway: AdminLeadPipelineReadGateway | null;
  readonly mutationGateway: AdminLeadPipelineMutationGateway | null;
}

type LaneRequestMode = "initial" | "retry" | "refresh" | "load-more";
type FeedbackByLeadId = ReadonlyMap<LeadId, AdminLeadPipelineFeedback>;

function initialLaneStates(): readonly AdminLeadPipelineLaneViewState[] {
  return leadPipelineStageOrder.map((pipelineStage) => ({
    kind: "loading",
    pipelineStage,
  }));
}

function lanePage(
  lane: AdminLeadPipelineLaneViewState,
): AdminLeadPipelineLanePage | null {
  return lane.kind === "ready"
    ? {
        pipelineStage: lane.pipelineStage,
        items: lane.items,
        nextCursor: lane.nextCursor,
      }
    : null;
}

function readyLanePages(
  lanes: readonly AdminLeadPipelineLaneViewState[],
): readonly AdminLeadPipelineLanePage[] | null {
  const pages = lanes.map(lanePage);
  if (pages.some((page) => page === null)) return null;
  return buildOrderedAdminLeadPipelineLanes(
    pages as readonly AdminLeadPipelineLanePage[],
  );
}

function readyIdleLanePages(
  lanes: readonly AdminLeadPipelineLaneViewState[],
): readonly AdminLeadPipelineLanePage[] | null {
  if (
    lanes.some(
      (lane) => lane.kind !== "ready" || lane.refreshing || lane.loadingMore,
    )
  ) {
    return null;
  }
  return readyLanePages(lanes);
}

function readyLaneLeadIdsAreUnique(
  lanes: readonly AdminLeadPipelineLaneViewState[],
): boolean {
  const leadIds = new Set<LeadId>();
  for (const lane of lanes) {
    if (lane.kind !== "ready") continue;
    for (const item of lane.items) {
      if (leadIds.has(item.leadId)) return false;
      leadIds.add(item.leadId);
    }
  }
  return true;
}

function replaceLane(
  lanes: readonly AdminLeadPipelineLaneViewState[],
  pipelineStage: LeadPipelineStage,
  nextLane: AdminLeadPipelineLaneViewState,
): readonly AdminLeadPipelineLaneViewState[] {
  return lanes.map((lane) =>
    lane.pipelineStage === pipelineStage ? nextLane : lane,
  );
}

function failedLaneState(
  current: AdminLeadPipelineLaneViewState,
  mode: LaneRequestMode,
): AdminLeadPipelineLaneViewState {
  if (current.kind !== "ready" || mode === "initial" || mode === "retry") {
    return { kind: "error", pipelineStage: current.pipelineStage };
  }

  return {
    ...current,
    refreshing: false,
    loadingMore: false,
    notice: mode === "refresh" ? "REFRESH_FAILED" : "LOAD_MORE_FAILED",
  };
}

function createPipelineIdempotencyKey(): IdempotencyKey | null {
  try {
    const value = globalThis.crypto?.randomUUID?.();
    return value && /^[A-Za-z0-9_-]{8,128}$/.test(value)
      ? (value as IdempotencyKey)
      : null;
  } catch {
    return null;
  }
}

function isPipelineMoveFailureOutcome(
  value: unknown,
): value is Extract<
  AdminLeadPipelineMoveOutcome,
  { kind: "conflict" | "rejected" }
> {
  if (typeof value !== "object" || value === null) return false;
  const kind = (value as { readonly kind?: unknown }).kind;
  return kind === "conflict" || kind === "rejected";
}

function AdminLeadPipelineFrame({
  children,
}: {
  readonly children: ReactNode;
}) {
  return (
    <section
      className="portal-page crm-pipeline-page portal-screen-only"
      aria-labelledby="portal-title"
      aria-describedby="crm-pipeline-description"
    >
      <header className="portal-page__header">
        <div>
          <p className="portal-kicker">CRM workspace</p>
          <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
            Pipeline
          </h1>
          <p id="crm-pipeline-description">
            Review active leads across the fixed workflow and confirm audited
            stage changes without inferring consent, appointments, or outcomes.
          </p>
        </div>
      </header>
      <AdminCrmViewNavigation current="pipeline" />
      <div className="portal-page__content">{children}</div>
    </section>
  );
}

function RouteState({
  kind,
  title,
  children,
  actions,
}: {
  readonly kind: "warning" | "error";
  readonly title: string;
  readonly children: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <ContentState
      kind={kind}
      title={title}
      actions={actions}
      live={kind === "error"}
    >
      {children}
    </ContentState>
  );
}

function AuthorizedAdminLeadPipeline({
  readGateway,
  mutationGateway,
}: {
  readonly readGateway: AdminLeadPipelineReadGateway;
  readonly mutationGateway: AdminLeadPipelineMutationGateway | null;
}) {
  const [lanes, setLanes] = useState(initialLaneStates);
  const [pendingLeadId, setPendingLeadId] = useState<LeadId | null>(null);
  const [feedbackByLeadId, setFeedbackByLeadId] = useState<FeedbackByLeadId>(
    () => new Map(),
  );
  const [announcementLeadId, setAnnouncementLeadId] = useState<LeadId | null>(
    null,
  );
  const [focusLeadId, setFocusLeadId] = useState<LeadId | null>(null);
  const readSequences = useRef(new Map<LeadPipelineStage, number>());
  const activeReads = useRef(new Map<LeadPipelineStage, AbortController>());
  const mutationSequence = useRef(0);
  const activeMutation = useRef<AbortController | null>(null);

  const requestLane = useCallback(
    async (
      pipelineStage: LeadPipelineStage,
      mode: LaneRequestMode,
      cursor: AdminLeadPipelineCursor | null,
    ) => {
      if (activeMutation.current) return;

      const requestId = (readSequences.current.get(pipelineStage) ?? 0) + 1;
      readSequences.current.set(pipelineStage, requestId);
      activeReads.current.get(pipelineStage)?.abort();

      const controller = new AbortController();
      activeReads.current.set(pipelineStage, controller);
      const protectedStateGeneration = captureProtectedStateGeneration();

      setLanes((current) => {
        const currentLane = current.find(
          (lane) => lane.pipelineStage === pipelineStage,
        )!;

        if (
          mode === "initial" ||
          mode === "retry" ||
          currentLane.kind !== "ready"
        ) {
          return replaceLane(current, pipelineStage, {
            kind: "loading",
            pipelineStage,
          });
        }

        return replaceLane(current, pipelineStage, {
          ...currentLane,
          refreshing: mode === "refresh",
          loadingMore: mode === "load-more",
          notice: null,
        });
      });

      try {
        const page = await readGateway(
          {
            pipelineStage,
            cursor,
            limit: ADMIN_LEAD_PIPELINE_PAGE_SIZE,
          },
          { signal: controller.signal },
        );

        if (
          controller.signal.aborted ||
          readSequences.current.get(pipelineStage) !== requestId ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }

        setLanes((current) => {
          const currentLane = current.find(
            (lane) => lane.pipelineStage === pipelineStage,
          )!;

          if (!isAdminLeadPipelineLanePageConsistent(page, pipelineStage)) {
            return replaceLane(
              current,
              pipelineStage,
              failedLaneState(currentLane, mode),
            );
          }

          if (
            mode === "load-more" &&
            (cursor === null ||
              page.nextCursor === cursor ||
              (page.nextCursor !== null &&
                currentLane.kind === "ready" &&
                currentLane.consumedCursors.includes(page.nextCursor)))
          ) {
            return replaceLane(
              current,
              pipelineStage,
              failedLaneState(currentLane, mode),
            );
          }

          const items =
            mode === "load-more" && currentLane.kind === "ready"
              ? mergeAdminLeadPipelineItems(currentLane.items, page.items)
              : page.items;
          if (items === null) {
            return replaceLane(
              current,
              pipelineStage,
              failedLaneState(currentLane, mode),
            );
          }
          const candidateLane: AdminLeadPipelineLaneViewState = {
            kind: "ready",
            pipelineStage,
            items,
            nextCursor: page.nextCursor,
            consumedCursors:
              mode === "load-more" &&
              currentLane.kind === "ready" &&
              cursor !== null
                ? [...currentLane.consumedCursors, cursor]
                : [],
            refreshing: false,
            loadingMore: false,
            notice: null,
          };
          const candidate = replaceLane(current, pipelineStage, candidateLane);

          if (
            !readyLaneLeadIdsAreUnique(candidate) ||
            (candidate.every((lane) => lane.kind === "ready") &&
              !readyLanePages(candidate))
          ) {
            return replaceLane(
              current,
              pipelineStage,
              failedLaneState(currentLane, mode),
            );
          }

          return candidate;
        });
      } catch {
        if (
          controller.signal.aborted ||
          readSequences.current.get(pipelineStage) !== requestId ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }

        setLanes((current) => {
          const currentLane = current.find(
            (lane) => lane.pipelineStage === pipelineStage,
          )!;
          return replaceLane(
            current,
            pipelineStage,
            failedLaneState(currentLane, mode),
          );
        });
      } finally {
        if (activeReads.current.get(pipelineStage) === controller) {
          activeReads.current.delete(pipelineStage);
        }
      }
    },
    [readGateway],
  );

  const refreshAllLanes = useCallback(() => {
    if (activeMutation.current) return;

    setFeedbackByLeadId(new Map());
    setAnnouncementLeadId(null);
    setFocusLeadId(null);
    for (const pipelineStage of leadPipelineStageOrder) {
      void requestLane(pipelineStage, "refresh", null);
    }
  }, [requestLane]);

  const moveLead = useCallback(
    async (item: AdminLeadPipelineItem, targetStage: LeadPipelineStage) => {
      if (
        !mutationGateway ||
        activeMutation.current ||
        !isLeadPipelineStage(targetStage) ||
        feedbackByLeadId.get(item.leadId)?.refreshRequired === true
      ) {
        return;
      }

      setAnnouncementLeadId(item.leadId);
      const confirmedPages = readyIdleLanePages(lanes);
      const idempotencyKey = createPipelineIdempotencyKey();
      if (!confirmedPages || !idempotencyKey) {
        setFeedbackByLeadId((current) => {
          const next = new Map(current);
          next.set(item.leadId, {
            tone: "critical",
            message:
              "The stage change could not be prepared. Refresh before trying again.",
            refreshRequired: true,
          });
          return next;
        });
        return;
      }

      const currentItem = confirmedPages
        .flatMap((page) => page.items)
        .find((candidate) => candidate.leadId === item.leadId);
      if (
        !currentItem ||
        currentItem.pipelineStage !== item.pipelineStage ||
        currentItem.pipelineRevision !== item.pipelineRevision ||
        currentItem.updatedAt !== item.updatedAt ||
        targetStage === item.pipelineStage
      ) {
        setAnnouncementLeadId(null);
        return;
      }

      const request: AdminLeadPipelineMoveRequest = {
        leadId: item.leadId,
        expectedStage: item.pipelineStage,
        expectedPipelineRevision: item.pipelineRevision,
        expectedUpdatedAt: item.updatedAt,
        targetStage,
      };
      const requestId = mutationSequence.current + 1;
      mutationSequence.current = requestId;
      for (const pipelineStage of leadPipelineStageOrder) {
        readSequences.current.set(
          pipelineStage,
          (readSequences.current.get(pipelineStage) ?? 0) + 1,
        );
      }
      for (const activeRead of activeReads.current.values()) {
        activeRead.abort();
      }
      activeReads.current.clear();
      const controller = new AbortController();
      activeMutation.current = controller;
      const protectedStateGeneration = captureProtectedStateGeneration();

      setPendingLeadId(item.leadId);
      setFeedbackByLeadId((current) => {
        const next = new Map(current);
        next.delete(item.leadId);
        return next;
      });

      try {
        const outcome = await mutationGateway(request, {
          signal: controller.signal,
          idempotencyKey,
        });

        if (
          controller.signal.aborted ||
          mutationSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }

        if (isPipelineMoveFailureOutcome(outcome)) {
          setFeedbackByLeadId((current) => {
            const next = new Map(current);
            next.set(item.leadId, {
              tone: "critical",
              message:
                outcome.kind === "conflict"
                  ? "This lead changed elsewhere. Refresh to confirm its current stage."
                  : "The stage change was not confirmed. Refresh before trying again.",
              refreshRequired: true,
            });
            return next;
          });
          return;
        }

        if (
          !isConfirmedAdminLeadPipelineMoveConsistent(
            currentItem,
            request,
            outcome,
          )
        ) {
          setFeedbackByLeadId((current) => {
            const next = new Map(current);
            next.set(item.leadId, {
              tone: "critical",
              message:
                "The stage change response could not be confirmed. Refresh the pipeline.",
              refreshRequired: true,
            });
            return next;
          });
          return;
        }

        const applied = applyConfirmedAdminLeadPipelineMove(
          confirmedPages,
          request,
          outcome,
        );
        if (!applied) {
          setFeedbackByLeadId((current) => {
            const next = new Map(current);
            next.set(item.leadId, {
              tone: "critical",
              message:
                "The confirmed stage could not be reconciled. Refresh the pipeline.",
              refreshRequired: true,
            });
            return next;
          });
          return;
        }

        setLanes(
          applied.map((page) => ({
            kind: "ready",
            pipelineStage: page.pipelineStage,
            items: page.items,
            nextCursor: null,
            consumedCursors: [],
            refreshing: false,
            loadingMore: false,
            notice: null,
          })),
        );
        setFeedbackByLeadId((current) => {
          const next = new Map(current);
          next.set(item.leadId, {
            tone: "positive",
            message: "The pipeline stage change was confirmed.",
            refreshRequired: false,
          });
          return next;
        });
        setFocusLeadId(item.leadId);
      } catch {
        if (
          controller.signal.aborted ||
          mutationSequence.current !== requestId ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }

        setFeedbackByLeadId((current) => {
          const next = new Map(current);
          next.set(item.leadId, {
            tone: "critical",
            message:
              "The stage change could not be confirmed. Refresh before trying again.",
            refreshRequired: true,
          });
          return next;
        });
      } finally {
        if (
          mutationSequence.current === requestId &&
          isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          activeMutation.current = null;
          setPendingLeadId(null);
        }
      }
    },
    [feedbackByLeadId, lanes, mutationGateway],
  );

  useEffect(() => {
    const clearPipelineState = (resetVisibleState: boolean) => {
      for (const pipelineStage of leadPipelineStageOrder) {
        readSequences.current.set(
          pipelineStage,
          (readSequences.current.get(pipelineStage) ?? 0) + 1,
        );
      }
      for (const controller of activeReads.current.values()) {
        controller.abort();
      }
      activeReads.current.clear();
      mutationSequence.current += 1;
      activeMutation.current?.abort();
      activeMutation.current = null;
      if (resetVisibleState) {
        setLanes(initialLaneStates());
        setPendingLeadId(null);
        setFeedbackByLeadId(new Map());
        setAnnouncementLeadId(null);
        setFocusLeadId(null);
      }
    };

    const unregisterCleaner = registerProtectedStateCleaner(
      "crm-admin-lead-pipeline",
      () => clearPipelineState(true),
    );

    for (const pipelineStage of leadPipelineStageOrder) {
      void requestLane(pipelineStage, "initial", null);
    }

    return () => {
      unregisterCleaner();
      clearPipelineState(false);
    };
  }, [requestLane]);

  const lanesConfirmedAndIdle = readyIdleLanePages(lanes) !== null;
  const mutationUnavailableReason = !mutationGateway
    ? "Stage changes require the authorized mutation service."
    : null;
  const movementDisabledReason = mutationUnavailableReason
    ? mutationUnavailableReason
    : !lanesConfirmedAndIdle
      ? "All ten stages must be confirmed before a stage change."
      : null;
  const clearFocusLead = useCallback(() => setFocusLeadId(null), []);

  return (
    <AdminLeadPipelineView
      lanes={lanes}
      mutationUnavailableReason={mutationUnavailableReason}
      movementDisabledReason={movementDisabledReason}
      pendingLeadId={pendingLeadId}
      feedbackByLeadId={feedbackByLeadId}
      announcementLeadId={announcementLeadId}
      focusLeadId={focusLeadId}
      onFocusSettled={clearFocusLead}
      onRefresh={refreshAllLanes}
      onRetryLane={(pipelineStage) =>
        void requestLane(pipelineStage, "retry", null)
      }
      onRefreshLane={(pipelineStage) =>
        void requestLane(pipelineStage, "refresh", null)
      }
      onLoadMore={(pipelineStage) => {
        if (activeMutation.current) return;
        const lane = lanes.find(
          (candidate) => candidate.pipelineStage === pipelineStage,
        );
        if (lane?.kind === "ready" && lane.nextCursor) {
          void requestLane(pipelineStage, "load-more", lane.nextCursor);
        }
      }}
      onMove={(item, targetStage) => void moveLead(item, targetStage)}
    />
  );
}

export function AdminLeadPipelineRoute({
  subject,
  readGateway,
  mutationGateway,
}: AdminLeadPipelineRouteProps) {
  if (!subject) {
    return (
      <AdminLeadPipelineFrame>
        <RouteState
          kind="warning"
          title="Secure sign-in is required."
          actions={<a href="/">Return to portal home</a>}
        >
          <p>
            Pipeline information stays unavailable until a verified portal
            session supplies an authorized account.
          </p>
        </RouteState>
      </AdminLeadPipelineFrame>
    );
  }

  if (!canAccessAdminCrm(subject)) {
    return (
      <AdminLeadPipelineFrame>
        <RouteState
          kind="warning"
          title="This account cannot open the CRM pipeline."
          actions={<a href="/">Return to portal home</a>}
        >
          <p>
            No pipeline information was requested or displayed. Use an approved
            account with CRM access.
          </p>
        </RouteState>
      </AdminLeadPipelineFrame>
    );
  }

  if (!readGateway) {
    return (
      <AdminLeadPipelineFrame>
        <RouteState
          kind="warning"
          title="The secure pipeline connection is not available yet."
          actions={<a href="/admin/crm/leads">Open the lead list</a>}
        >
          <p>
            The route and permission gate are ready, but the authorized bounded
            pipeline read service has not been connected. No sample or cached
            cards are shown.
          </p>
        </RouteState>
      </AdminLeadPipelineFrame>
    );
  }

  return (
    <AdminLeadPipelineFrame>
      <AuthorizedAdminLeadPipeline
        key={adminCrmAuthorizationKey(subject)}
        readGateway={readGateway}
        mutationGateway={
          canAccessAdminCrm(subject, "write") ? mutationGateway : null
        }
      />
    </AdminLeadPipelineFrame>
  );
}
